// src/config/colors.ts
export type ColorKey = 'white' | 'black' | 'red' | 'green' | 'blue' | 'yellow' | 'orange' | 'pink' | 'purple';
export type SupportedLanguage = 'en' | 'es' | 'fr' | 'de' | 'it' | 'pl' | 'pt' | 'pt-BR' | 'nl' | 'ja';

export interface ColorData {
  name: string;
  hex: string;
  urlPath: string;
}

export interface ColorConfig {
  [lang: string]: {
    [color: string]: ColorData;
  };
}

export type ColorNameMap = {
  [K in SupportedLanguage]: {
    [C in ColorKey]: string;
  };
};

export const COLOR_STORAGE_KEY = 'selectedColor';
export const DEFAULT_COLOR = '#FFFFFF';

export const colorNameMapping: ColorNameMap = {
  en: {
    white: 'White Screen',
    black: 'Black Screen',
    red: 'Red Screen',
    green: 'Green Screen',
    blue: 'Blue Screen',
    yellow: 'Yellow Screen',
    orange: 'Orange Screen',
    pink: 'Pink Screen',
    purple: 'Purple Screen'
  },
  es: {
    white: 'Pantalla blanca',
    black: 'Pantalla negra',
    red: 'Pantalla roja',
    green: 'Pantalla verde',
    blue: 'Pantalla azul',
    yellow: 'Pantalla amarilla',
    orange: 'Pantalla naranja',
    pink: 'Pantalla rosa',
    purple: 'Pantalla morada'
  },
  fr: {
    white: 'Écran blanc',
    black: 'Écran noir',
    red: 'Écran rouge',
    green: 'Écran vert',
    blue: 'Écran bleu',
    yellow: 'Écran jaune',
    orange: 'Écran orange',
    pink: 'Écran rose',
    purple: 'Écran violet'
  },
  de: {
    white: 'Weißer Bildschirm',
    black: 'Schwarzer Bildschirm',
    red: 'Roter Bildschirm',
    green: 'Grüner Bildschirm',
    blue: 'Blauer Bildschirm',
    yellow: 'Gelber Bildschirm',
    orange: 'Oranger Bildschirm',
    pink: 'Rosa Bildschirm',
    purple: 'Violetter Bildschirm'
  },
  it: {
    white: 'Schermo bianco',
    black: 'Schermo nero',
    red: 'Schermo rosso',
    green: 'Schermo verde',
    blue: 'Schermo blu',
    yellow: 'Schermo giallo',
    orange: 'Schermo arancione',
    pink: 'Schermo rosa',
    purple: 'Schermo viola'
  },
  pl: {
    white: 'Biały ekran',
    black: 'Czarny ekran',
    red: 'Czerwony ekran',
    green: 'Zielony ekran',
    blue: 'Niebieski ekran',
    yellow: 'Żółty ekran',
    orange: 'Pomarańczowy ekran',
    pink: 'Różowy ekran',
    purple: 'Fioletowy ekran'
  },
  pt: {
    white: 'Tela branca',
    black: 'Tela preta',
    red: 'Tela vermelha',
    green: 'Tela verde',
    blue: 'Tela azul',
    yellow: 'Tela amarela',
    orange: 'Tela laranja',
    pink: 'Tela rosa',
    purple: 'Tela roxa'
  },
  'pt-BR': {
    white: 'Tela branca',
    black: 'Tela preta',
    red: 'Tela vermelha',
    green: 'Tela verde',
    blue: 'Tela azul',
    yellow: 'Tela amarela',
    orange: 'Tela laranja',
    pink: 'Tela rosa',
    purple: 'Tela roxa'
  },
  nl: {
    white: 'Wit scherm',
    black: 'Zwart scherm',
    red: 'Rood scherm',
    green: 'Groen scherm',
    blue: 'Blauw scherm',
    yellow: 'Geel scherm',
    orange: 'Oranje scherm',
    pink: 'Roze scherm',
    purple: 'Paars scherm'
  },
  ja: {
    white: '白い画面',
    black: '黒い画面',
    red: '赤い画面',
    green: '緑の画面',
    blue: '青い画面',
    yellow: '黄色い画面',
    orange: 'オレンジの画面',
    pink: 'ピンクの画面',
    purple: '紫の画面'
  }
} as const;

export const colorConfig: ColorConfig = {
  en: {
    white: { name: 'White Screen', hex: '#FFFFFF', urlPath: 'white-screen' },
    black: { name: 'Black Screen', hex: '#000000', urlPath: 'black-screen' },
    red: { name: 'Red Screen', hex: '#FF0000', urlPath: 'red-screen' },
    green: { name: 'Green Screen', hex: '#00FF00', urlPath: 'green-screen' },
    blue: { name: 'Blue Screen', hex: '#0000FF', urlPath: 'blue-screen' },
    yellow: { name: 'Yellow Screen', hex: '#FFFF00', urlPath: 'yellow-screen' },
    orange: { name: 'Orange Screen', hex: '#FFA500', urlPath: 'orange-screen' },
    pink: { name: 'Pink Screen', hex: '#FFC0CB', urlPath: 'pink-screen' },
    purple: { name: 'Purple Screen', hex: '#800080', urlPath: 'purple-screen' }
  },
  es: {
    white: { name: 'Pantalla blanca', hex: '#FFFFFF', urlPath: 'pantalla-blanca' },
    black: { name: 'Pantalla negra', hex: '#000000', urlPath: 'pantalla-negra' },
    red: { name: 'Pantalla roja', hex: '#FF0000', urlPath: 'pantalla-roja' },
    green: { name: 'Pantalla verde', hex: '#00FF00', urlPath: 'pantalla-verde' },
    blue: { name: 'Pantalla azul', hex: '#0000FF', urlPath: 'pantalla-azul' },
    yellow: { name: 'Pantalla amarilla', hex: '#FFFF00', urlPath: 'pantalla-amarilla' },
    orange: { name: 'Pantalla naranja', hex: '#FFA500', urlPath: 'pantalla-naranja' },
    pink: { name: 'Pantalla rosa', hex: '#FFC0CB', urlPath: 'pantalla-rosa' },
    purple: { name: 'Pantalla morada', hex: '#800080', urlPath: 'pantalla-morada' }
  },
  fr: {
    white: { name: 'Écran blanc', hex: '#FFFFFF', urlPath: 'ecran-blanc' },
    black: { name: 'Écran noir', hex: '#000000', urlPath: 'ecran-noir' },
    red: { name: 'Écran rouge', hex: '#FF0000', urlPath: 'ecran-rouge' },
    green: { name: 'Écran vert', hex: '#00FF00', urlPath: 'ecran-vert' },
    blue: { name: 'Écran bleu', hex: '#0000FF', urlPath: 'ecran-bleu' },
    yellow: { name: 'Écran jaune', hex: '#FFFF00', urlPath: 'ecran-jaune' },
    orange: { name: 'Écran orange', hex: '#FFA500', urlPath: 'ecran-orange' },
    pink: { name: 'Écran rose', hex: '#FFC0CB', urlPath: 'ecran-rose' },
    purple: { name: 'Écran violet', hex: '#800080', urlPath: 'ecran-violet' }
  },
  de: {
    white: { name: 'Weißer Bildschirm', hex: '#FFFFFF', urlPath: 'weisser-bildschirm' },
    black: { name: 'Schwarzer Bildschirm', hex: '#000000', urlPath: 'schwarzer-bildschirm' },
    red: { name: 'Roter Bildschirm', hex: '#FF0000', urlPath: 'roter-bildschirm' },
    green: { name: 'Grüner Bildschirm', hex: '#00FF00', urlPath: 'gruener-bildschirm' },
    blue: { name: 'Blauer Bildschirm', hex: '#0000FF', urlPath: 'blauer-bildschirm' },
    yellow: { name: 'Gelber Bildschirm', hex: '#FFFF00', urlPath: 'gelber-bildschirm' },
    orange: { name: 'Oranger Bildschirm', hex: '#FFA500', urlPath: 'oranger-bildschirm' },
    pink: { name: 'Rosa Bildschirm', hex: '#FFC0CB', urlPath: 'rosa-bildschirm' },
    purple: { name: 'Violetter Bildschirm', hex: '#800080', urlPath: 'violetter-bildschirm' }
  },
  it: {
    white: { name: 'Schermo bianco', hex: '#FFFFFF', urlPath: 'schermo-bianco' },
    black: { name: 'Schermo nero', hex: '#000000', urlPath: 'schermo-nero' },
    red: { name: 'Schermo rosso', hex: '#FF0000', urlPath: 'schermo-rosso' },
    green: { name: 'Schermo verde', hex: '#00FF00', urlPath: 'schermo-verde' },
    blue: { name: 'Schermo blu', hex: '#0000FF', urlPath: 'schermo-blu' },
    yellow: { name: 'Schermo giallo', hex: '#FFFF00', urlPath: 'schermo-giallo' },
    orange: { name: 'Schermo arancione', hex: '#FFA500', urlPath: 'schermo-arancione' },
    pink: { name: 'Schermo rosa', hex: '#FFC0CB', urlPath: 'schermo-rosa' },
    purple: { name: 'Schermo viola', hex: '#800080', urlPath: 'schermo-viola' }
  },
  pl: {
    white: { name: 'Biały ekran', hex: '#FFFFFF', urlPath: 'bialy-ekran' },
    black: { name: 'Czarny ekran', hex: '#000000', urlPath: 'czarny-ekran' },
    red: { name: 'Czerwony ekran', hex: '#FF0000', urlPath: 'czerwony-ekran' },
    green: { name: 'Zielony ekran', hex: '#00FF00', urlPath: 'zielony-ekran' },
    blue: { name: 'Niebieski ekran', hex: '#0000FF', urlPath: 'niebieski-ekran' },
    yellow: { name: 'Żółty ekran', hex: '#FFFF00', urlPath: 'zolty-ekran' },
    orange: { name: 'Pomarańczowy ekran', hex: '#FFA500', urlPath: 'pomaranczowy-ekran' },
    pink: { name: 'Różowy ekran', hex: '#FFC0CB', urlPath: 'rozowy-ekran' },
    purple: { name: 'Fioletowy ekran', hex: '#800080', urlPath: 'fioletowy-ekran' }
  },
  pt: {
    white: { name: 'Tela branca', hex: '#FFFFFF', urlPath: 'tela-branca' },
    black: { name: 'Tela preta', hex: '#000000', urlPath: 'tela-preta' },
    red: { name: 'Tela vermelha', hex: '#FF0000', urlPath: 'tela-vermelha' },
    green: { name: 'Tela verde', hex: '#00FF00', urlPath: 'tela-verde' },
    blue: { name: 'Tela azul', hex: '#0000FF', urlPath: 'tela-azul' },
    yellow: { name: 'Tela amarela', hex: '#FFFF00', urlPath: 'tela-amarela' },
    orange: { name: 'Tela laranja', hex: '#FFA500', urlPath: 'tela-laranja' },
    pink: { name: 'Tela rosa', hex: '#FFC0CB', urlPath: 'tela-rosa' },
    purple: { name: 'Tela roxa', hex: '#800080', urlPath: 'tela-roxa' }
  },
  'pt-BR': {
    white: { name: 'Tela branca', hex: '#FFFFFF', urlPath: 'tela-branca' },
    black: { name: 'Tela preta', hex: '#000000', urlPath: 'tela-preta' },
    red: { name: 'Tela vermelha', hex: '#FF0000', urlPath: 'tela-vermelha' },
    green: { name: 'Tela verde', hex: '#00FF00', urlPath: 'tela-verde' },
    blue: { name: 'Tela azul', hex: '#0000FF', urlPath: 'tela-azul' },
    yellow: { name: 'Tela amarela', hex: '#FFFF00', urlPath: 'tela-amarela' },
    orange: { name: 'Tela laranja', hex: '#FFA500', urlPath: 'tela-laranja' },
    pink: { name: 'Tela rosa', hex: '#FFC0CB', urlPath: 'tela-rosa' },
    purple: { name: 'Tela roxa', hex: '#800080', urlPath: 'tela-roxa' }
  },
  nl: {
    white: { name: 'Wit scherm', hex: '#FFFFFF', urlPath: 'wit-scherm' },
    black: { name: 'Zwart scherm', hex: '#000000', urlPath: 'zwart-scherm' },
    red: { name: 'Rood scherm', hex: '#FF0000', urlPath: 'rood-scherm' },
    green: { name: 'Groen scherm', hex: '#00FF00', urlPath: 'groen-scherm' },
    blue: { name: 'Blauw scherm', hex: '#0000FF', urlPath: 'blauw-scherm' },
    yellow: { name: 'Geel scherm', hex: '#FFFF00', urlPath: 'geel-scherm' },
    orange: { name: 'Oranje scherm', hex: '#FFA500', urlPath: 'oranje-scherm' },
    pink: { name: 'Roze scherm', hex: '#FFC0CB', urlPath: 'roze-scherm' },
    purple: { name: 'Paars scherm', hex: '#800080', urlPath: 'paars-scherm' }
  },
  ja: {
    white: { name: '白い画面', hex: '#FFFFFF', urlPath: 'shiro-gamen' },
    black: { name: '黒い画面', hex: '#000000', urlPath: 'kuro-gamen' },
    red: { name: '赤い画面', hex: '#FF0000', urlPath: 'aka-gamen' },
    green: { name: '緑の画面', hex: '#00FF00', urlPath: 'midori-gamen' },
    blue: { name: '青い画面', hex: '#0000FF', urlPath: 'ao-gamen' },
    yellow: { name: '黄色い画面', hex: '#FFFF00', urlPath: 'kiiro-gamen' },
    orange: { name: 'オレンジの画面', hex: '#FFA500', urlPath: 'orenji-gamen' },
    pink: { name: 'ピンクの画面', hex: '#FFC0CB', urlPath: 'pinku-gamen' },
    purple: { name: '紫の画面', hex: '#800080', urlPath: 'murasaki-gamen' }
  }
};

export const getColorByHex = (hex: string): ColorKey => {
  const defaultColor = 'white' as ColorKey;
  
  for (const [colorKey, colorData] of Object.entries(colorConfig.en)) {
    if (colorData.hex.toLowerCase() === hex.toLowerCase()) {
      return colorKey as ColorKey;
    }
  }
  
  return defaultColor;
};

export const getColorPathForLanguage = (hex: string, lang: SupportedLanguage): string => {
  const colorKey = getColorByHex(hex);
  return colorConfig[lang]?.[colorKey]?.urlPath || colorConfig.en[colorKey].urlPath;
};

export const getColorHexFromPath = (path: string, lang: SupportedLanguage): string => {
  const langConfig = colorConfig[lang] || colorConfig.en;
  
  for (const colorData of Object.values(langConfig)) {
    if (colorData.urlPath === path) {
      return colorData.hex;
    }
  }
  
  return DEFAULT_COLOR;
};

export const getColorNameForLanguage = (hex: string, lang: SupportedLanguage): string => {
  const colorKey = getColorByHex(hex);
  return colorNameMapping[lang]?.[colorKey] || colorNameMapping.en[colorKey];
};